import React, { useEffect, useState } from 'react';
import { Clock, Cake, AlertTriangle } from 'lucide-react';

const DURATION_MS = 120 * 60 * 1000;           // 120 minutes
const AMBER_MS    = 30 * 60 * 1000;            // last 30 min
const RED_MS      = 10 * 60 * 1000;            // last 10 min
const CAKE_WINDOW_MS = { early: 25 * 60 * 1000, late: 20 * 60 * 1000 }; // T-25..T-20

/**
 * PartyTimer — inline live countdown for an arrived party row.
 * Given `arrivedAt` (ISO string), shows MM:SS remaining out of 120 min.
 * Styling flips green → amber at T-30 → red at T-10, and a "🍰 Cake time soon"
 * tag appears in the last 25-minute window to remind the kitchen.
 */
const PartyTimer = ({ arrivedAt, testId }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const start = arrivedAt ? new Date(arrivedAt).getTime() : null;
  if (!start || Number.isNaN(start)) return null;

  const elapsed = now - start;
  const remaining = DURATION_MS - elapsed;
  const ended = remaining <= 0;

  const bar = Math.max(0, Math.min(1, elapsed / DURATION_MS));

  let color;
  let label;
  if (ended) {
    color = '#6B6B7A';
    label = 'Wrap up';
  } else if (remaining <= RED_MS) {
    color = '#EF4444';
    label = 'Final stretch';
  } else if (remaining <= AMBER_MS) {
    color = '#FF7A2E';
    label = 'Winding down';
  } else {
    color = '#06D6A0';
    label = 'Party on';
  }

  const showCakeHint = !ended && remaining <= CAKE_WINDOW_MS.early && remaining > CAKE_WINDOW_MS.late;

  const mins = Math.max(0, Math.floor(remaining / 60000));
  const secs = Math.max(0, Math.floor((remaining % 60000) / 1000));
  const mmss = `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={testId}>
      <div
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold tabular-nums"
        style={{ backgroundColor: `${color}15`, color }}
        aria-label={`${ended ? 'Wrap up' : `${mmss} remaining`}`}
      >
        {remaining <= RED_MS && !ended ? (
          <AlertTriangle className="w-3 h-3" />
        ) : (
          <Clock className="w-3 h-3" />
        )}
        <span>{ended ? 'Wrap up' : `${mmss} left`}</span>
      </div>

      {/* Tiny progress bar */}
      <span className="w-16 h-1.5 rounded-full bg-[#E8E5DF] overflow-hidden" aria-hidden>
        <span
          className="block h-full rounded-full transition-[width] duration-1000 ease-linear"
          style={{ width: `${bar * 100}%`, backgroundColor: color }}
        />
      </span>

      <span className="text-[10px] uppercase tracking-wider" style={{ color }}>{label}</span>

      {showCakeHint && (
        <span
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold"
          style={{ backgroundColor: '#FFF4D6', color: '#A8780F' }}
          data-testid={`${testId || 'party-timer'}-cake-hint`}
          title="Time to rally the kitchen for cake service."
        >
          <Cake className="w-3 h-3" /> Cake time soon
        </span>
      )}
    </div>
  );
};

export default PartyTimer;
